/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined, WarningOutlined } from '@ant-design/icons'
import { Modal, Alert } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useMemo } from 'react'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { isCanlan } from '@sportninja/common/utils/customer-name'

interface UnsavedChangesModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
  error: string | null
}

const UnsavedChangesModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  error,
}: UnsavedChangesModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  /**
   * Handles modal close and resets state
   */
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10007,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10006,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.WARNING_300};
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          Unsaved Changes
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
              style={`
                font-family: Inter, Arial, Helvetica, sans-serif;
                font-size: 16px;
                font-weight: 400;
                line-height: 23.2px;
              `}
            >
              You have unsaved sections or questions. If you continue, the
              information will be lost.
            </Text>
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
            `}
          />
        )}

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            flex-direction: column;
            gap: 16px;
            display: inline-flex;
            width: 100%;
          `}
        >
          <button
            css={css`
              width: 100%;
              flex: 1 1 0;
              padding: 8px 24px;
              border-radius: 8px;
              border: 2px solid ${colors.WHITE};
              background: transparent;
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={handleClose}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              CANCEL
            </Text>
          </button>
          <button
            css={css`
              width: 100%;
              flex: 1 1 0;
              padding: 8px 24px;
              background: ${colors.WARNING_300};
              border-radius: 8px;
              border: 2px solid ${colors.WARNING_300};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.5 : 1};
              transition: all 0.2s;
              &:hover {
                opacity: ${isLoading ? 0.5 : 0.8};
              }
            `}
            onClick={!isLoading ? onConfirm : undefined}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              // WARNING_300 (#ffc107) is a base color — the same amber on both
              // tenants — so the label is dark rather than tenant-switched.
              // White on this fill is ~1.7:1 contrast; SECONDARY_500 is ~11:1.
              color={colors.SECONDARY_500}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              {isLoading ? 'SAVING...' : 'CONTINUE WITHOUT SAVING'}
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default UnsavedChangesModal
